import React from 'react';
import { Drawer, Descriptions, Divider, Table, Typography, Tag } from 'antd';
import dayjs from 'dayjs';
import { JournalEntry, JournalEntryLine } from '../../../services/accountingService';
import { formatCurrency } from './journalEntriesHelpers';

const { Text } = Typography;

export interface JournalEntryDetailDrawerProps {
  entry: JournalEntry | null;
  visible: boolean;
  loading: boolean;
  onClose: () => void;
}

export const JournalEntryDetailDrawer: React.FC<JournalEntryDetailDrawerProps> = ({
  entry,
  visible,
  loading,
  onClose,
}) => {
  const detailLinesColumns = [
    {
      title: 'Cuenta',
      key: 'account',
      width: 190,
      render: (_: any, line: JournalEntryLine) => (
        <div>
          <Text strong>{line.account_code}</Text>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {line.account_name}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Tercero',
      dataIndex: 'contact_name',
      key: 'contact_name',
      width: 130,
      ellipsis: true,
      render: (text: string) => text || <Text type="secondary">—</Text>,
    },
    {
      title: 'Detalle',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Débito',
      dataIndex: 'debit',
      key: 'debit',
      width: 135,
      align: 'right' as const,
      render: (val: number) => (
        <span style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
          {val > 0 ? (
            <Text style={{ color: '#1677ff', whiteSpace: 'nowrap' }}>
              {formatCurrency(val)}
            </Text>
          ) : (
            <Text type="secondary" style={{ whiteSpace: 'nowrap' }}>
              0.00
            </Text>
          )}
        </span>
      ),
    },
    {
      title: 'Crédito',
      dataIndex: 'credit',
      key: 'credit',
      width: 135,
      align: 'right' as const,
      render: (val: number) => (
        <span style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
          {val > 0 ? (
            <Text style={{ color: '#722ed1', whiteSpace: 'nowrap' }}>
              {formatCurrency(val)}
            </Text>
          ) : (
            <Text type="secondary" style={{ whiteSpace: 'nowrap' }}>
              0.00
            </Text>
          )}
        </span>
      ),
    },
  ];

  const titleText = entry
    ? `Comprobante Contable: ${entry.entry_number || (entry as any).number || `CC-${entry.id}`}`
    : 'Detalle del Comprobante';

  return (
    <Drawer
      title={titleText}
      width={840}
      open={visible}
      onClose={onClose}
      loading={loading}
    >
      {entry && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label="Comprobante N°">
              <Text strong>
                {entry.entry_number || (entry as any).number || `CC-${entry.id}`}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Fecha">
              {entry.entry_date
                ? dayjs(entry.entry_date).format('DD/MM/YYYY')
                : (entry as any).date
                ? dayjs((entry as any).date).format('DD/MM/YYYY')
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Concepto" span={2}>
              {entry.concept || (entry as any).description || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Referencia">
              {entry.reference_type
                ? `${entry.reference_type} #${entry.reference_id}`
                : 'Manual'}
            </Descriptions.Item>
            <Descriptions.Item label="Estado">
              <Tag color={entry.status === 'POSTED' ? 'green' : 'gold'}>
                {entry.status === 'POSTED' ? 'ASENTADO' : entry.status}
              </Tag>
            </Descriptions.Item>
          </Descriptions>

          <Divider style={{ margin: '8px 0' }}>Partidas Contables</Divider>

          <Table
            dataSource={entry.items || entry.lines || []}
            columns={detailLinesColumns}
            rowKey="id"
            pagination={false}
            size="small"
            summary={(pageData) => {
              const totalDeb = pageData.reduce(
                (prev, curr) => prev + Number(curr.debit || 0),
                0
              );
              const totalCred = pageData.reduce(
                (prev, curr) => prev + Number(curr.credit || 0),
                0
              );
              return (
                <Table.Summary.Row
                  style={{ background: '#fafafa', fontWeight: 600 }}
                >
                  <Table.Summary.Cell index={0} colSpan={3} align="right">
                    TOTALES:
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">
                    <Text
                      style={{
                        color: '#1677ff',
                        whiteSpace: 'nowrap',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {formatCurrency(totalDeb)}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right">
                    <Text
                      style={{
                        color: '#722ed1',
                        whiteSpace: 'nowrap',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {formatCurrency(totalCred)}
                    </Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              );
            }}
          />
        </div>
      )}
    </Drawer>
  );
};
